// ADR 5.9.26p §2 — the email + password pipeline, once, for both panels.
//
// Sign-in and registration differ by one field on the wire (`accepted_terms`)
// and by one endpoint. Everything before that is identical and is the expensive
// part: fetch a challenge, solve its proof-of-work, derive the argon2id
// prehash. Keeping it here is what lets `SignInPanel` and `RegisterPanel` be
// forms rather than two copies of a protocol.
//
// The two rules it drives are `rust-core`'s and are pinned by 5.9.26l's
// vectors; ADR 9.9.26t made them the *same* rules rather than twins of them, so
// this module now calls the wasm facade. It is still only their caller, and
// AC1/AC8 test the *body* it builds.
//
// ADR 25.9.26f §3 — through `import('../wasm')`, never statically. The sign-in
// and register panels are the signed-out first paint, and a static edge here
// would hold that paint for the whole wasm download. `pow.worker.ts` set the
// precedent; the import starts early enough that the download overlaps the
// challenge and the proof-of-work, which runs in its own worker realm.

import { challenge, login, register, type AuthResponse } from '../api/auth'
import { POW_SOLVE_TIMEOUT_MS } from '../offline/requestDeadline'
import { reportNonFatal } from '../telemetry/capture'
import { signInCost } from '../telemetry/auth'
import { createStageTimer, type StageTimer } from '../telemetry/signinCost'
import type { PowRequest, PowResult } from './pow.worker'

/** The prehash generation this client speaks. */
export const AUTH_VERSION = 2

/** Which half of the pipeline a submission is asking for. */
export type CredentialIntent = 'login' | 'register'

/** What the panels show while the CPU work runs — never a spinner with no cause. */
export type CredentialStage = 'challenge' | 'proving' | 'hashing' | 'submitting'

export interface CredentialSubmission {
  intent: CredentialIntent
  email: string
  password: string
  onStage?: (stage: CredentialStage) => void
}

/**
 * ADR 2.10.26s §4 — a Worker solve that did not answer within its ceiling.
 *
 * Deliberately **not** a `RequestTimeoutError`: a stuck worker is a CPU
 * problem, not a network one, so it raises no offline banner, starts no
 * `/health` probe and never reads as "no response" to the transport's readers.
 */
export class SolveTimeoutError extends Error {
  readonly timeoutMs: number

  constructor(timeoutMs: number) {
    super(`proof-of-work solve timed out after ${timeoutMs} ms`)
    this.name = 'SolveTimeoutError'
    this.timeoutMs = timeoutMs
  }
}

function solveTimedOut(worker: Worker, request: PowRequest): SolveTimeoutError {
  worker.terminate()
  reportNonFatal(`proof-of-work solve timed out after ${POW_SOLVE_TIMEOUT_MS} ms`, ['SolveTimeoutError', 'pow-solve'], {
    timeout_ms: String(POW_SOLVE_TIMEOUT_MS),
    difficulty: String(request.difficulty),
  })
  return new SolveTimeoutError(POW_SOLVE_TIMEOUT_MS)
}

function workerSolve(request: PowRequest): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const worker = new Worker(new URL('./pow.worker.ts', import.meta.url), { type: 'module' })
    // The search is memoryless, so a fresh challenge after the ceiling costs no
    // more than continuing would have: the timeout costs a slow device a tap.
    const timer = setTimeout(() => reject(solveTimedOut(worker, request)), POW_SOLVE_TIMEOUT_MS)
    worker.addEventListener('message', (event: MessageEvent<PowResult>) => {
      clearTimeout(timer)
      resolve(event.data.solution)
      worker.terminate()
    })
    worker.addEventListener('error', (event) => {
      clearTimeout(timer)
      reject(new Error(event.message))
      worker.terminate()
    })
    worker.postMessage(request)
  })
}

/**
 * Solve `challenge` in a worker, or inline where there is none.
 *
 * jsdom has no `Worker`, and neither does a browser with workers disabled by
 * policy. The fallback is the same function the worker runs, so the answer is
 * identical — only the thread it blocks differs. That inline solve cannot be
 * interrupted (it blocks the thread a timer would run on), so only the worker
 * has a ceiling (ADR 2.10.26s §4).
 */
export function solveChallenge(request: PowRequest): Promise<string> {
  if (typeof Worker === 'undefined') {
    return import('../wasm').then(({ solvePow }) => solvePow(request.nonce, request.difficulty))
  }
  return workerSolve(request)
}

/**
 * Everything both panels do before they differ: a challenge, its solution, and
 * the prehash the server compares — the password itself never leaves the tab.
 *
 * ADR 6.9.26j §4: it times **its own** stage transitions, and returns the
 * `difficulty` the server priced. The existing `onStage` callback is the
 * *caller's* (`onStage: setStage`, `CredentialFields.tsx`) and receives only a
 * stage name, so neither timing nor difficulty is visible anywhere outside this
 * function — which is also the only reason the event can carry `difficulty` at
 * all.
 */
async function buildLoginBody(
  email: string,
  password: string,
  onStage: (stage: CredentialStage) => void,
  timer: StageTimer,
) {
  // ADR 25.9.26f §3 — started first, so the download overlaps the round trip
  // and the solve. The `.catch` only keeps a failure that lands before the
  // `await` below from reading as unhandled; the `await` still rejects with it.
  const facade = import('../wasm')
  facade.catch(() => {})
  onStage('challenge')
  const { nonce, difficulty } = await challenge()

  onStage('proving')
  timer.mark('proving')
  const solution = await solveChallenge({ nonce, difficulty })
  // Awaited *before* the `hashing` mark: a residual wait for the wasm is spent
  // on the `proving` stage the learner is already watching and lands in
  // `proving_ms`, so `hashing_ms` stays an argon2 cost (25.9.26f §5, AC12).
  const { deriveKey } = await facade

  onStage('hashing')
  timer.mark('hashing')
  const prehash = await deriveKey(password, email)
  timer.mark('done')

  return {
    difficulty,
    body: {
      email,
      prehash,
      auth_version: AUTH_VERSION,
      pow_nonce: nonce,
      pow_solution: solution,
    },
  }
}

/**
 * Sign in or register, and return the session the server minted.
 *
 * A failure is thrown, never retried: the proof-of-work is spent and the
 * challenge may be replay-protected, so an automatic second attempt would both
 * cost the user seconds and stand a good chance of being refused for a
 * different reason than the first.
 *
 * ADR 6.9.26j §4: exactly one `web_signin_cost` per attempt, **on both
 * outcomes**. A thrown `submitCredentials` still knows both timings, because
 * both stages completed before the request was sent — and that refused-after-
 * paying case is the one this event exists to find.
 */
export async function submitCredentials(submission: CredentialSubmission): Promise<AuthResponse> {
  const { intent, email, password, onStage = () => {} } = submission
  const timer = createStageTimer()
  const { difficulty, body } = await buildLoginBody(email, password, onStage, timer)
  onStage('submitting')
  try {
    const response =
      intent === 'register' ? await register({ ...body, accepted_terms: true }) : await login(body)
    signInCost({ ...timer.timings(), difficulty, auth_method: 'email', outcome: 'ok' })
    return response
  } catch (cause) {
    signInCost({ ...timer.timings(), difficulty, auth_method: 'email', outcome: 'failed' })
    throw cause
  }
}
